import { queryOptions } from "@tanstack/react-query";

import { type DirectoryParams, type DirectoryUser, listDirectory } from "@/features/directory/api";

/**
 * The tenant's people, indexed by id.
 *
 * Cached for a long time on purpose: a colleague's display name changes
 * about as often as their job title, and refetching it per version row
 * would make a ten-version history ten requests. `staleTime` is what
 * turns `userLabel` from a network call into a map lookup.
 */
export function directoryQuery(params: DirectoryParams = {}) {
  return queryOptions({
    queryKey: ["directory", params],
    queryFn: () => listDirectory(params),
    staleTime: 5 * 60 * 1000,
    select: (users: DirectoryUser[]) => ({
      all: users,
      byId: new Map(users.map((user) => [user.id, user])),
    }),
  });
}

/**
 * A readable name for a user id, falling back to the id itself.
 *
 * The fallback matters more than it looks: an id that resolves to nothing
 * is a person who was deleted from a tenant they still have history in,
 * and showing the raw uuid is what an admin would search the audit log
 * for anyway. Blank would be a worse answer than PR5's raw ids, not a
 * better one.
 */
export function nameFor(
  directory: { byId: Map<string, DirectoryUser> } | undefined,
  userId: string | null | undefined,
): string | undefined {
  if (userId === null || userId === undefined) return undefined;
  const user = directory?.byId.get(userId);
  if (user === undefined) return userId;
  return user.display_name ?? user.email;
}
